import { useRef, useState } from 'react';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { LocalAllowListForm } from '#components/patterns/config-overrides/forms/LocalAllowForm.tsx';
import { RemoteAllowListForm } from '#components/patterns/config-overrides/forms/RemoteAllowForm.tsx';
import type { ConfigKey } from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { ConfigOverride } from '#dn-types/host.ts';
import { GraphiteForm } from '#screens/tags/form/config-overrides/stats-forms/GraphiteForm.tsx';
import { PrometheusForm } from '#screens/tags/form/config-overrides/stats-forms/PrometheusForm.tsx';
import { makeUniqueCIDRRequiredValidator } from '#util/validation/validators/uniqueCIDRRequired.ts';
import { makeUniqueNameRequiredValidator } from '#util/validation/validators/uniqueNameRequired.ts';
import { ConfigChooser } from './ConfigChooser.tsx';
import { ConfigOverrideForm } from './override-form/ConfigOverrideForm.tsx';

type Props = {
  onCancel: () => void;
  onSave: (values: ConfigOverride[]) => void;
  /** Need all of these so we can validate unique cidrs */
  existingOverrides: ConfigOverride[];
};

export function AddConfigOverrideModal({ onSave, onCancel, existingOverrides }: Props) {
  const modalRef = useRef(null);
  const [configType, setConfigType] = useState<ConfigKey | null>(null);
  const [chooserError, setChooserError] = useState<string>();

  const remoteCIDRs = existingOverrides.flatMap((o) =>
    o.key === 'lighthouse.remote_allow_list' ? Object.keys(o.value) : []
  );
  const localAllowNames = existingOverrides.flatMap((o) => {
    if (o.key === 'lighthouse.local_allow_list') {
      const { interfaces, ...cidrs } = o.value;
      return [...Object.keys(cidrs), ...Object.keys(interfaces)];
    }
    return [];
  });

  return (
    <Modal isOpen={true} onClose={onCancel} ref={modalRef}>
      <ModalHeader>Add config</ModalHeader>
      <ModalBody>
        <Spacer direction="vertical" size="wide">
          <ConfigChooser
            value={configType ?? ''}
            existingOverrides={existingOverrides}
            onChange={(v) => {
              setChooserError(undefined);
              setConfigType(v);
            }}
            errorMessage={chooserError}
          />
          {configType === 'lighthouse.remote_allow_list' ?
            <RemoteAllowListForm
              onSave={(data) => {
                onSave([{ key: configType, value: { [data.cidr]: data.allow } }]);
              }}
              cidrValidation={makeUniqueCIDRRequiredValidator(remoteCIDRs)}
            />
          : configType === 'lighthouse.local_allow_list' ?
            <LocalAllowListForm
              onSave={(data) => {
                if ('cidr' in data) {
                  onSave([{ key: configType, value: { interfaces: {}, [data.cidr]: data.allow } }]);
                } else {
                  onSave([{ key: configType, value: { interfaces: { [data.interface]: data.allow } } }]);
                }
              }}
              cidrValidation={makeUniqueNameRequiredValidator(localAllowNames)}
            />
          : configType === 'prometheus' ?
            <PrometheusForm onSave={onSave} />
          : configType === 'graphite' ?
            <GraphiteForm onSave={onSave} />
          : configType ?
            <ConfigOverrideForm overrideKey={configType} onSave={onSave} />
          : null}
        </Spacer>
      </ModalBody>
      <ModalFooter>
        <ConfirmButtonGroup
          variant="right"
          primaryButton={
            configType ?
              <Button type="submit" form={`${configType}-form`}>
                Add
              </Button>
              // If the user hasn't selected a type yet, we need to show an error message
            : <Button
                onClick={() => {
                  setChooserError('What kind of config would you like to set?');
                }}
              >
                Add
              </Button>
          }
          secondaryButton={
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
          }
        />
      </ModalFooter>
    </Modal>
  );
}
